// 本地相簿(APP#42)的純邏輯 —— 與 IndexedDB / DOM 解耦的可測純函式。
//
// 相簿頁瀏覽「本機截圖 / 錄影」(capture-to-local, APP#17)。這些純函式負責:
//   - 影音種類判定 / 縮圖模型
//   - 播放倍速清單與循環切換(影片檢視器)
//   - 時間 / 大小 顯示格式
// 真正碰 IndexedDB 的副作用收在 galleryDb.ts,React 狀態編排收在 useGallery.ts。

/** 相簿一筆媒體的種類。 */
export type GalleryKind = 'photo' | 'video';

/** 相簿一筆媒體(存於 IndexedDB;blob 為實際位元組)。 */
export interface GalleryItem {
  /** 唯一鍵(uuid)。 */
  id: string;
  kind: GalleryKind;
  /** 實際媒體位元組(png / mp4 / webm …)。 */
  blob: Blob;
  /** MIME(image/png、video/mp4、video/webm …)。 */
  mime: string;
  /** 下載用檔名(deviceName_時間戳.ext),對齊 capture 的 captureFilename。 */
  filename: string;
  /** 來源裝置 id(裝置詳情頁進來可據此過濾);帳號頁總覽為空。 */
  deviceId?: string;
  /** 來源裝置顯示名(存檔當下快照)。 */
  deviceName: string;
  /** 建立時間(ms epoch)。 */
  createdAt: number;
  /** 位元組大小。 */
  size: number;
}

/** 不含 blob 的輕量中繼(清單排序 / 顯示用,避免搬動大 blob)。 */
export type GalleryMeta = Omit<GalleryItem, 'blob'>;

/** 影片檢視器可選的播放倍速(對齊常見播放器)。 */
export const PLAYBACK_RATES: readonly number[] = [0.5, 1, 1.5, 2];

/**
 * 循環切到下一個倍速(檢視器「倍速」按鈕:0.5→1→1.5→2→0.5…)。
 * 傳入的 current 不在清單時退回 1x 的下一個。
 */
export function nextPlaybackRate(current: number, rates: readonly number[] = PLAYBACK_RATES): number {
  const i = rates.indexOf(current);
  if (i < 0) {
    const base = rates.indexOf(1);
    return rates[(base + 1) % rates.length] ?? rates[0] ?? 1;
  }
  return rates[(i + 1) % rates.length] ?? rates[0] ?? 1;
}

/** 倍速顯示標籤(1 → "1x"、1.5 → "1.5x")。 */
export function formatRate(rate: number): string {
  return `${rate}x`;
}

/** MIME → 種類(video/* → video,其餘視為 photo)。 */
export function kindFromMime(mime: string | null | undefined): GalleryKind {
  return mime && mime.startsWith('video/') ? 'video' : 'photo';
}

/** 位元組 → 人類可讀(B / KB / MB)。相簿卡片顯示檔案大小用。 */
export function formatSize(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/**
 * 建立時間 → 本地日期字串(相簿分組標題用)。
 * 回傳「YYYY-MM-DD」以便相同日期歸為一組。
 */
export function dayKey(createdAt: number): string {
  const d = new Date(createdAt);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** 建立時間 → 時:分(卡片副標)。 */
export function timeLabel(createdAt: number): string {
  const d = new Date(createdAt);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 依 createdAt 由新到舊排序(回新陣列,不改原陣列)。 */
export function sortByNewest<T extends { createdAt: number }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => b.createdAt - a.createdAt);
}

/** 選填 deviceId 過濾(空 → 全部;有值 → 只該裝置)。 */
export function filterByDevice<T extends { deviceId?: string }>(
  items: readonly T[],
  deviceId: string | undefined,
): T[] {
  if (!deviceId) return [...items];
  return items.filter((it) => it.deviceId === deviceId);
}

/**
 * 依日期分組(由新到舊;同日內也由新到舊)。相簿頁用日期分段標題呈現。
 * 回傳 [{ day, items }]。
 */
export function groupByDay<T extends { createdAt: number }>(items: readonly T[]): { day: string; items: T[] }[] {
  const sorted = sortByNewest(items);
  const groups: { day: string; items: T[] }[] = [];
  const index = new Map<string, { day: string; items: T[] }>();
  for (const it of sorted) {
    const key = dayKey(it.createdAt);
    let g = index.get(key);
    if (!g) {
      g = { day: key, items: [] };
      index.set(key, g);
      groups.push(g);
    }
    g.items.push(it);
  }
  return groups;
}
